import { t } from '@avalara/bat-apps-shared/i18n';
import { SCol, SRow } from '@avalara/skylab-sdk/react';

import { addYears, format } from 'date-fns';
import { Fragment, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { isFormIncomplete } from '../../lib/shared-signup';
import { getReturnsSalesTax } from '../../lib/side-effects/avatax';
import { selectState } from '../../selectors';
import { getOrderSummary } from '../../selectors/order';
import { selectReturnsPricingData } from '../../selectors/returns';
import Discount from '../offer/order-summary/discount';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import OrderSummaryHeader from '../offer/order-summary/order-summary-header';
import { GreyTermsBox, OrangeHorizontalRule, OrderSummaryContainer } from '../offer/styled/order-summary';

const OrderSummary = () => {
  const dispatch = useAppDispatch();

  const {
    account: { accountId },
    order: { discountName, discountPercent },
    sharedSignup,
  } = useSelector(selectState);

  const { discountAmount, pricePerReturn, subtotalDisplayText, salestaxDisplayText, totalPrice, subtotal } =
    useSelector(selectReturnsPricingData);

  const renewalDate = format(addYears(new Date(), 1), 'P');

  const { showDiscountedSubTotal } = useSelector(getOrderSummary);

  const isDiscountApplied = !!discountPercent && discountPercent > 0;
  const formIncomplete = isFormIncomplete(sharedSignup);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    dispatch(getReturnsSalesTax());
  }, [accountId, formIncomplete]);

  return (
    <Fragment>
      <OrderSummaryContainer>
        <OrderSummaryHeader />

        <SRow className='justify-content-end align-items-center order-summary-text pad-left-none pad-top-sm pad-bottom-xs space-between'>
          <SCol span='8'>{t('client.general.annual-subscription')}</SCol>
          <SCol span='4' className={`text-right ${isDiscountApplied ? ' text-strikethrough' : ''}`}>
            <span>{subtotalDisplayText}</span>
          </SCol>
        </SRow>
        {isDiscountApplied && (
          <Discount
            currency={'USD'}
            discountAmount={discountAmount}
            discountName={discountName}
            showDiscountedSubTotal={showDiscountedSubTotal}
            subtotal={subtotal}
          />
        )}
        <SRow className='justify-content-end align-items-center order-summary-text pad-left-none pad-top-xs pad-bottom-xs space-between'>
          <SCol span='6'>{t('client.general.sales-tax')}</SCol>
          <SCol
            span='6'
            className={`text-right${/calculated/i.test(salestaxDisplayText) ? ' font-italic text-sm' : ''}`}
          >
            <span>{salestaxDisplayText}</span>
          </SCol>
        </SRow>
        <SRow className='justify-content-end order-summary-text pad-bottom-sm pad-left-none pad-top-xs space-between'>
          <SCol span='8'>
            <span>
              {t('client.general.e-filing-fee')}
              <p className='font-italic smaller-text'>{'per return as incurred'}</p>
            </span>
          </SCol>
          <SCol span='4' className='text-right'>
            <span>{pricePerReturn}</span>
          </SCol>
        </SRow>
        <OrangeHorizontalRule className='margin-left-xs margin-right-xs' />
        <SRow className='justify-content-end align-items-center order-summary-text pad-top-sm pad-left-none pad-bottom-none'>
          <SCol span='8'>
            <span>{t('client.general.total-due-today')}</span>
          </SCol>
          <SCol span='4' className='text-right'>
            <span>{totalPrice}</span>
          </SCol>
        </SRow>
        <SRow>
          <GreyTermsBox>
            <p className='text-sm'>{t('client.general.billing-terms-1')}</p>
            <p className='pad-top-xs small-height small-text'>
              {t('client.general.automatic-renewal-billed-to-your-payment-method-on')}
              <span>{renewalDate}</span>
            </p>
            <p className='pad-bottom-xs small-text'>{t('client.general.filing-fees-will-be-applied-as-incurred')}</p>
          </GreyTermsBox>
        </SRow>
      </OrderSummaryContainer>
    </Fragment>
  );
};

export default OrderSummary;
